import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  PortalLoadingCard,
  PortalMetricCard,
  PortalStateCard,
  formatPortalDate,
  getStatusChipStyle
} from './portalShared'

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    fontWeight: 700,
    color: '#20342b'
  },
  mutedText: {
    color: '#5d6c64'
  },
  statusBreakdown: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    padding: theme.spacing(1.5, 0)
  },
  statusLabel: {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#6a7a72',
    marginRight: theme.spacing(0.5)
  },
  tableContainer: {
    borderRadius: 8,
    border: '1px solid #dce6df',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
  },
  tableHead: {
    backgroundColor: '#f7faf7'
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: '0.72rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#6a7a72',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #dce6df'
  },
  tableCell: {
    borderBottom: '1px solid #eef2ef',
    verticalAlign: 'top',
    padding: theme.spacing(1.25, 2)
  },
  propertyName: {
    fontWeight: 600,
    color: '#20342b'
  },
  propertyAddress: {
    color: '#5d6c64',
    fontSize: '0.82rem'
  },
  propertyNotes: {
    color: '#7a8a80',
    fontSize: '0.78rem',
    fontStyle: 'italic',
    marginTop: theme.spacing(0.5)
  }
}))

const statusOrder = ['overdue', 'never_done', 'needs_completion', 'due_soon', 'ok', 'ignored']

const SubcontractorDashboard = () => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [dashboard, setDashboard] = useState(null)

  const loadDashboard = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const response = await axios.get('/api/subcontractor/dashboard')
      setDashboard(response.data || null)
    } catch (requestError) {
      console.error('Failed to load subcontractor dashboard', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load dashboard data right now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  const summary = dashboard?.summary || {}
  const scheduleSummary = summary.schedule || {}
  const nextUp = dashboard?.nextUp || []

  const nextScheduledDate = useMemo(() => {
    const datedItems = nextUp.filter(item => item?.nextScheduledDate)
    if (!datedItems.length) {
      return 'No upcoming date'
    }

    const sorted = [...datedItems].sort((a, b) => new Date(a.nextScheduledDate) - new Date(b.nextScheduledDate))
    return formatPortalDate(sorted[0].nextScheduledDate, { includeWeekday: true })
  }, [nextUp])

  if (loading) {
    return <PortalLoadingCard title="Loading dashboard" message="Pulling your subcontractor scope and upcoming herbicide work." minHeight={280} />
  }

  if (error) {
    return (
      <PortalStateCard
        title="Dashboard unavailable"
        message={error}
        actionLabel="Retry"
        onAction={loadDashboard}
        tone="danger"
        minHeight={280}
      />
    )
  }

  if (!dashboard) {
    return (
      <PortalStateCard
        title="No dashboard data"
        message="This account does not currently have any subcontractor dashboard data to display."
        actionLabel="Refresh"
        onAction={loadDashboard}
        minHeight={260}
      />
    )
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <PortalMetricCard
          eyebrow="Visible Users"
          value={summary.activeUsers || 0}
          caption="Employees currently in scope for this portal account."
          accent="green"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PortalMetricCard
          eyebrow="Crew Scope"
          value={summary.crews || 0}
          caption="Distinct crews represented in the visible subcontractor schedule."
          accent="gold"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PortalMetricCard
          eyebrow="Visible Jobs"
          value={scheduleSummary.total || 0}
          caption="Schedule items currently returned by the subcontractor API."
          accent="blue"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PortalMetricCard
          eyebrow="Next Date"
          value={nextScheduledDate}
          caption="Earliest upcoming scheduled work from the current dashboard feed."
          accent="plum"
        />
      </Grid>

      <Grid item xs={12}>
        <Box className={classes.statusBreakdown}>
          <Typography className={classes.statusLabel}>Status breakdown</Typography>
          {statusOrder.map((status) => (
            <Chip
              key={status}
              size="small"
              label={`${status.replace(/_/g, ' ')}: ${scheduleSummary[status] || 0}`}
              style={getStatusChipStyle(status)}
            />
          ))}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Next Up
        </Typography>
        <Box mt={1.5}>
          {!nextUp.length ? (
            <PortalStateCard
              title="No visible schedule items"
              message="The current subcontractor scope does not have any upcoming herbicide work yet."
              minHeight={220}
            />
          ) : (
            <TableContainer className={classes.tableContainer}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell}>Property</TableCell>
                    <TableCell className={classes.tableHeadCell}>Territory</TableCell>
                    <TableCell className={classes.tableHeadCell}>Next Date</TableCell>
                    <TableCell className={classes.tableHeadCell}>Last Applied</TableCell>
                    <TableCell className={classes.tableHeadCell}>Status</TableCell>
                    <TableCell className={classes.tableHeadCell}>Gate Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nextUp.map((item) => (
                    <TableRow key={item._id}>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2" className={classes.propertyName}>
                          {item.siteName || 'Unnamed property'}
                        </Typography>
                        <Typography className={classes.propertyAddress}>
                          {item.address || 'No address on file'}
                        </Typography>
                        {item.notes ? (
                          <Typography className={classes.propertyNotes}>
                            {item.notes}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2">
                          {item.territoryName || 'Unassigned'}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2">
                          {formatPortalDate(item.nextScheduledDate, { includeWeekday: true })}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2">
                          {formatPortalDate(item.lastAppliedDate, { fallback: 'None recorded' })}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Box display="flex" flexDirection="column" gridGap={4}>
                          <Chip
                            size="small"
                            label={item.statusLabel || item.status}
                            style={getStatusChipStyle(item.status)}
                          />
                          {item.contractedForHerbicide ? (
                            <Chip
                              size="small"
                              label="Contracted"
                              style={{ backgroundColor: '#edf4ff', color: '#23417e' }}
                            />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="body2">
                          {item.gateCode || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(SubcontractorDashboard)
